import { styled } from 'styled-components';

import { theme } from '../../styles';
import { ForaLogo, TelephoneIcon } from '../../assets/icons';
import type { Colors } from '../../utils';
import { getColor, ResponsiveSpacing } from '../../utils';
import { Container, Flex, Grid, Spacer, VerticalSpacer } from '../Layout';
import { Line } from '../Line';
import { Link } from '../Link';
import { useMediaMax, useMediaMin } from '../Media';
import { Text } from '../Text';

const StyledFooter = styled.footer<{ backgroundColor: Colors }>`
  background-color: ${props => getColor(props.backgroundColor)};
`;

type FooterProps = {
  testId?: string;
  paddingLeft?: ResponsiveSpacing;
  paddingRight?: ResponsiveSpacing;
  maxWidth?: ResponsiveSpacing;
};

export const Footer = ({
  testId,
  paddingLeft = [20, 46, 46],
  paddingRight = [20, 46, 46],
  maxWidth = 1196,
}: FooterProps) => {
  const isMobile = useMediaMax(theme.breakpoint.md);
  const isTablet = useMediaMax(theme.breakpoint.lg);
  const isDesktop = useMediaMin(theme.breakpoint.lg);

  return (
    <StyledFooter backgroundColor="darkBlue" data-testid={testId}>
      <Container
        marginLeft="auto"
        marginRight="auto"
        paddingTop={[44, 60, 60]}
        paddingBottom={[40, 40, 40]}
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        maxWidth={maxWidth}
      >
        <Grid gutterWidth={40}>
          {isDesktop && (
            <Spacer marginBottom={18}>
              <ForaLogo width={112} />
            </Spacer>
          )}
          <Grid.Row textAlign={['center', 'center', 'left']}>
            <Grid.Col xs={12} md={8} lg={4} flexOrder={[2, 2, 1]} push={{ lg: 0, md: 2 }}>
              {isTablet && (
                <Spacer marginBottom={18} marginTop={[0, 50, 0]}>
                  <ForaLogo width={112} />
                </Spacer>
              )}
              <Text size="small" color="white" marginBottom={[30, 40, 34]}>
                Fora är den oberoende valcentralen för Avtalspension SAF-LO som guidar företag och anställda rätt i sin
                kollektivavtalade pensions- och försäkringslösning.
              </Text>
              <Grid.Row gutterWidth={40}>
                <Grid.Col xs={12} md={6}>
                  <Spacer marginBottom={[30, 0, 0]}>
                    <Text size="small" color="white" marginBottom={4} fontWeight="semibold">
                      Postadress
                    </Text>
                    <Text size="small" color="white">
                      Fora AB, 101 56 Stockholm
                    </Text>
                  </Spacer>
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                  <Text size="small" color="white" marginBottom={4} fontWeight="semibold">
                    Organisationsnummer
                  </Text>
                  <Text size="small" color="white">
                    556541-8356
                  </Text>
                </Grid.Col>
              </Grid.Row>
            </Grid.Col>
            <Grid.Col
              xs={12}
              md={4}
              lg={2}
              push={{ lg: 2 }}
              flexOrder={[1, 1, 2]}
              horizontalAlign={['center', 'center', 'flex-start']}
            >
              <Text size="small" marginBottom={14} color="white" fontWeight={isDesktop ? 'semibold' : 'bold'}>
                KONTAKTA OSS
              </Text>
              <Text size="small" color="white" marginBottom={[10, 0, 0]}>
                Kundservice
              </Text>
              <Link
                testId="test-link-customer-support-telephone"
                styleAs={isMobile ? 'button' : 'link'}
                fontSize="small"
                variant="transparent"
                to="tel:+4687874010"
                hasIcon={false}
                fullWidth={false}
              >
                {isMobile && <TelephoneIcon width={17} color="white" marginRight={10} />}08-787 40 10
              </Link>
              {isMobile && <Line marginTop={30} marginBottom={30} width={100} />}
            </Grid.Col>
            <Grid.Col
              xs={12}
              md={4}
              lg={2}
              push={{ lg: 2 }}
              flexOrder={[1, 1, 2]}
              horizontalAlign={['center', 'center', 'flex-start']}
            >
              <Text size="small" marginBottom={14} color="white" fontWeight={isDesktop ? 'semibold' : 'bold'}>
                OM FORA
              </Text>
              <Link
                testId="test-link-about-fora"
                to="https://fora.se/om-fora"
                variant="transparent"
                fontSize="small"
                hasIcon={false}
              >
                Om Fora
              </Link>
              <VerticalSpacer marginBottom={14} />
              <Link
                testId="test-link-legal-information"
                to="https://fora.se/juridisk-information"
                variant="transparent"
                fontSize="small"
                hasIcon={false}
              >
                Juridisk information
              </Link>
              {isMobile && <Line marginTop={30} marginBottom={30} width={100} />}
            </Grid.Col>
            <Grid.Col
              xs={12}
              md={4}
              lg={2}
              push={{ lg: 2 }}
              flexOrder={[1, 1, 2]}
              horizontalAlign={['center', 'center', 'flex-start']}
            >
              <Text size="small" marginBottom={14} color="white" fontWeight={isDesktop ? 'semibold' : 'bold'}>
                OM MINA SIDOR
              </Text>
              <Link
                testId="test-link-general-terms"
                to="https://fora.se/bf000226"
                variant="transparent"
                fontSize="small"
                hasIcon={false}
              >
                Allmänna villkor
              </Link>
              <VerticalSpacer marginBottom={14} />
              <Link
                testId="test-link-personal-data-gdpr"
                to="https://fora.se/personuppgifter"
                variant="transparent"
                fontSize="small"
                hasIcon={false}
              >
                Så hanterar Fora personuppgifter
              </Link>
              <VerticalSpacer marginBottom={14} />
              <Link
                testId="test-link-cookies-information"
                to="https://fora.se/cookies"
                variant="transparent"
                fontSize="small"
                hasIcon={false}
              >
                Information om cookies
              </Link>
              {isMobile && <Line marginTop={30} marginBottom={30} width={100} />}
            </Grid.Col>
          </Grid.Row>
        </Grid>
        <Line marginTop={60} marginBottom={15} marginLeft={[-20, 0, 0]} marginRight={[-20, 0, 0]} />
        <Flex justifyContent={isDesktop ? 'flex-end' : 'center'}>
          <Text size="small" color="white">
            © {new Date().getFullYear().toString()} Fora AB
          </Text>
        </Flex>
      </Container>
    </StyledFooter>
  );
};
